import { Button, ButtonVariant } from "@src/components/button/Button";
import { ChooseRule } from "@src/components/chooseRule/ChooseRule";
import helperStyles from "@src/components/helpers.module.scss";
import { Switch } from "@src/components/switch/Switch";
import { clx } from "@src/components/utils/stringUtils";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { ISavedRules } from "@src/store/pricing/savedRulesSlice";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
import styles from "./ManageListing.module.scss";
import { Dropdown, IItems } from "@src/components/dropdown/Dropdown";
import MarketplacesV2 from "@src/components/marketPlaces/MarketplacesV2";
import {
	isAnyListingInTMRestrictedCategory,
	isMarketplaceBlockedByTMConsent,
	syncLinkedDestinations,
	toggleDestinationSelection,
} from "@src/components/utils/marketplaceUtils";
import { ExportDestination } from "@src/constants/constants";
import { MyTooltip } from "@src/components/tooltip/Tooltip";
import { IGlobalInventoryRawItem } from "@src/store/pricing/queryGlobalInventorySlice";

interface INamedShareUnshare {
	isSold?: boolean;
	listingId?: number;
	disableCancelButton?: boolean;
	onSuccess?: () => void;
}

type TState = {
	selectedExportDestinations: Array<number>;
	hideSeats: boolean;
	splits: number;
};

const NamedShareUnshare: React.FC<INamedShareUnshare> = ({ listingId, isSold, disableCancelButton, onSuccess }) => {
	const dispatch = useAppDispatch();

	const { data: marketplaces } = useAppSelector((state) => state.system.exportDestinations);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);
	const { loading: shareListingsLoading } = useAppSelector((state) => state.pricing.listingsSharing);
	const eventsList = useAppSelector((state) => state.pricing.events.data.Events);
	const eventInventoryRecords = useAppSelector((state) => state.pricing.eventInventoryAll.data);

	const currentListing = eventInventoryRecords.find((x) => x.Listing_ID === listingId);
	const inRestrictedCategory = currentListing ? isAnyListingInTMRestrictedCategory([currentListing], eventsList) : false;

	// A listing on hold cannot be re-shared — broadcast destinations are hidden and sharing
	// is blocked until the hold is released (TAC-2491). Event inventory exposes On_Hold /
	// Consignor_Hold booleans; global inventory rows carry Num_OnHold / Num_Consignor_Hold counts.
	const globalListing = currentListing as IGlobalInventoryRawItem | undefined;
	const isOnHold = !!(
		currentListing?.On_Hold ||
		currentListing?.Consignor_Hold ||
		(globalListing?.Num_OnHold ?? 0) > 0 ||
		(globalListing?.Num_Consignor_Hold ?? 0) > 0
	);

	const [state, setState] = useState<TState>({
		selectedExportDestinations: [],
		hideSeats: false,
		splits: -1,
	});

	const handleExportDestinationToggle = (id: number) => {
		setState((prev) => ({
			...prev,
			selectedExportDestinations: toggleDestinationSelection(prev.selectedExportDestinations, id),
		}));
	};

	const handleShareEverywhere = () => {
		const marketplacesMap = marketplaces
			.filter((x) => !isMarketplaceBlockedByTMConsent(x, inRestrictedCategory))
			.filter((x) => hasCIMSMarketAccess || x.Export_Destination_ID !== ExportDestination.Cims)
			.map((x) => x.Export_Destination_ID);
		setState((prev) => ({ ...prev, selectedExportDestinations: marketplacesMap }));
	};

	const handleUnshareEverywhere = () => {
		setState((prev) => ({ ...prev, selectedExportDestinations: [] }));
	};

	const handleChangeSplits = (x: TState["splits"]) => {
		setState((prev) => ({ ...prev, splits: x }));
	};

	const splits: Array<IItems> = [
		{ id: 1, label: "Default", value: -1, onClick: () => handleChangeSplits(-1) },
		{ id: 2, label: "Pairs", value: 2, onClick: () => handleChangeSplits(2) },
		{ id: 3, label: "Any", value: 1, onClick: () => handleChangeSplits(1) },
		{ id: 4, label: "None", value: 0, onClick: () => handleChangeSplits(0) },
	];

	const buttonText = splits.find((x) => x.value === state.splits)?.label ?? "Default";

	const handleSubmit = async () => {
		if (!listingId) return;
		// Never fire a sharing request for a listing that is on hold.
		if (isOnHold) return;

		const Visibility = state.hideSeats ? ("HIDE" as const) : ("SHOW" as const);

		const blockedDestinationIds = new Set(
			marketplaces.filter((m) => isMarketplaceBlockedByTMConsent(m, inRestrictedCategory)).map((m) => m.Export_Destination_ID),
		);

		const ShareOutSettings = syncLinkedDestinations(state.selectedExportDestinations)
			.filter((x) => !blockedDestinationIds.has(x))
			.filter((x) => hasCIMSMarketAccess || x !== ExportDestination.Cims)
			.map((x) => ({
				Export_Destination_ID: x,
				AllowedSplits: state.splits,
				Visibility,
			}));

		const payload = {
			ListingIds: [listingId],
			ShareOutSettings,
		};

		const res = await dispatch(pricingActions.updateSharing(payload));

		if (res.meta.requestStatus === "fulfilled") {
			onSuccess?.();
		}
	};

	const handleRulePick = (x: ISavedRules) => {
		setState((prev) => ({
			...prev,
			selectedExportDestinations: syncLinkedDestinations(x.Markups.map((x) => x.Export_Destination_ID)),
			splits: x.AllowedSplits,
			hideSeats: x.Visibility === "HIDE" ? true : false,
		}));
	};

	const handleRuleSave = (RuleName: string) => {
		const Markups = state.selectedExportDestinations.map((x) => ({ Export_Destination_ID: x }));
		const Visibility = state.hideSeats === true ? "HIDE" : "SHOW";
		const AllowedSplits = state.splits;

		const payload = {
			AllowedSplits,
			Visibility,
			Markups,
			RuleName,
		};

		dispatch(pricingActions.saveRule(payload));
	};

	useEffect(() => {
		if (!listingId) return;

		const run = async () => {
			const res = await dispatch(pricingActions.getListingSharedSettings([listingId]));

			if (pricingActions.getListingSharedSettings.fulfilled.match(res)) {
				const first = res.payload[0];

				setState((prev) => ({
					...prev,
					selectedExportDestinations: syncLinkedDestinations(res.payload.map((x) => x.Export_Destination_ID)),
					splits: first ? first.AllowedSplits : prev.splits,
					hideSeats: first ? first.Visibility === "HIDE" : prev.hideSeats,
				}));
			}
		};

		run();
	}, [dispatch, listingId]);

	return (
		<div data-is-sold={!!isSold} className={styles.shareUnshareContainer}>
			<div className={styles.left}>
				<div className={clx(styles.subHeader, helperStyles.ellipsisDiv, styles.mb)}>Choose Rule</div>
				<div className={clx(styles.mb)}>
					<ChooseRule disableHeadline onRuleSave={handleRuleSave} onRulePick={handleRulePick} />
				</div>
				<div className={clx(styles.subHeader, helperStyles.ellipsisDiv, styles.mb)}>Listing Rules</div>
				<div className={styles.listingRulesContainer}>
					<Dropdown className={styles.dropdown} isDisabled={false} items={splits} buttonText={buttonText} />
					<Button variant={ButtonVariant.Secondary}>
						<Switch
							label="Hide Seats"
							checked={state.hideSeats}
							onChange={(e) => setState((prev) => ({ ...prev, hideSeats: e }))}
						/>
					</Button>
				</div>
				<div className={styles.broadcastHeader}>
					<div className={clx(styles.subHeader, helperStyles.ellipsisDiv)}>Broadcast Destinations</div>
					{isOnHold ? null : (
						<div className={helperStyles.flexSmallGap}>
							{state.selectedExportDestinations.length ? (
								<div className={styles.shareSelect} onClick={handleUnshareEverywhere}>
									Unshare Everywhere
								</div>
							) : (
								<div className={styles.shareSelect} onClick={handleShareEverywhere}>
									Share Everywhere
								</div>
							)}
						</div>
					)}
				</div>
				{isOnHold ? (
					<div className={helperStyles.secondaryText} style={{ fontSize: "1.4rem", padding: "6px 0" }}>
						Broadcast destinations are hidden while this listing is on hold.
					</div>
				) : (
					<MarketplacesV2
						selectedExportDestinations={state.selectedExportDestinations}
						onMarkteplaceClick={handleExportDestinationToggle}
						containerClassName={styles.marketplacesContainer}
						compactView
						inRestrictedCategory={inRestrictedCategory}
					/>
				)}
				<div className={styles.buttons}>
					{disableCancelButton ? null : (
						<Button onClick={onSuccess} variant={ButtonVariant.Secondary}>
							Cancel
						</Button>
					)}
					{isOnHold ? (
						<MyTooltip
							id={`share-on-hold-${listingId ?? "none"}`}
							popup="This listing is on hold. Release the hold to update broadcast destinations."
							elementStyle={{ display: "inline-flex" }}
							element={
								<span>
									<Button onClick={handleSubmit} disabled>
										Apply sharing
									</Button>
								</span>
							}
						/>
					) : (
						<Button onClick={handleSubmit} isLoading={shareListingsLoading} disabled={!listingId || shareListingsLoading}>
							Apply sharing
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export const ShareUnshare = memo(NamedShareUnshare);
